import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useDispatch, useSelector } from 'react-redux'
import { useParams } from 'react-router-dom'
import { useKindeAuth } from '@kinde-oss/kinde-auth-react'
import { ArrowForwardRounded } from '@mui/icons-material'
import { Box, Button, Card, CardActions, CardContent, CardHeader, TextField, Typography } from '@mui/material'
import { useSnackbar } from 'notistack'
import PropTypes from 'prop-types'

import { useLazyGetContactQuery, useLazyGetCustomerQuery } from '@/app/services/customer'
import { useRequestAccessCodeMutation, useValidateAccessCodeMutation } from '@/app/services/guestUser'
import {
    selectOpenGuestLoginDialog,
    setGuestUserData,
    setGuestUserLoggedIn,
    setOpenGuestLoginDialog,
} from '@/app/slices/guestUserSlice'
import { selectOrganisation, selectOrganisationId } from '@/app/slices/organisationSlice'
import { acceptedPatterns } from '@/common/helpers/formatUtilities'
import { Paths } from '@/common/utils'

const classes = {
    logInInput: {
        marginBottom: 2,
    },
    loginAction: {
        paddingInline: 3,
        paddingBlockEnd: 3,
    },
}

const GuestLogin = ({ onCancelClose, onSuccessClose }) => {
    const { t } = useTranslation()
    const { enqueueSnackbar } = useSnackbar()
    const { login, register } = useKindeAuth()
    const { orderId } = useParams()
    const dispatch = useDispatch()

    const organisationId = useSelector(selectOrganisationId)
    const storeSettings = useSelector(selectOrganisation)
    const openGuestLoginDialog = useSelector(selectOpenGuestLoginDialog)

    const [requestAccessCode, { isLoading: isRequesting }] = useRequestAccessCodeMutation()
    const [validateAccessCode, { isLoading: isValidating }] = useValidateAccessCodeMutation()
    const [getCustomer] = useLazyGetCustomerQuery()
    const [getContact] = useLazyGetContactQuery()

    const [loginStep, setLoginStep] = useState('requestAccessCode')
    const [email, setEmail] = useState('')
    const [accessCode, setAccessCode] = useState('')
    const [error, setError] = useState(false)
    const [errorMessage, setErrorMessage] = useState('')

    function isValidEmail(email) {
        return acceptedPatterns.email.test(email)
    }

    const handleEmailChange = (event) => {
        if (!isValidEmail(event.target.value)) {
            setError(true)
            setErrorMessage(t('Please enter a valid email address'))
        } else {
            setError(false)
            setErrorMessage('')
        }
        setEmail(event.target.value)
    }

    const handleRequestAccessCode = async () => {
        if (isValidEmail(email)) {
            try {
                await requestAccessCode({
                    organisationId,
                    emailAddress: email,
                }).unwrap()
                enqueueSnackbar(t('A verification code has been sent to this email address'), { variant: 'success' })
                setLoginStep('validateAccessCode')
            } catch (_error) {
                const errorMessage = t('A verification code could not be sent at this time. Please try again later.')
                setError(true)
                setErrorMessage(errorMessage)
                enqueueSnackbar(errorMessage, { variant: 'error', autoHideDuration: 5000 })
            }
        }
    }

    const handleValidateAccessCode = async () => {
        try {
            const response = await validateAccessCode({
                organisationId,
                emailAddress: email,
                code: accessCode,
            }).unwrap()

            const customer = await getCustomer({
                organisationId,
                customerId: response.customerId,
            }).unwrap()

            const contact = await getContact({
                contactId: response.contactId,
                customerId: response.customerId,
                organisationId,
            }).unwrap()

            dispatch(setGuestUserLoggedIn())
            dispatch(setGuestUserData({ ...customer, ...contact }))

            if (onSuccessClose && typeof onSuccessClose === 'function') {
                onSuccessClose(response)
            }

            dispatch(setOpenGuestLoginDialog(null))

            enqueueSnackbar(t('Email address verified'), { variant: 'success' })
        } catch (error) {
            const errorMessage = t('The code you entered is not valid. Please request a new verification code.')

            setAccessCode('')
            setError(true)
            setErrorMessage(errorMessage)
            enqueueSnackbar(errorMessage, { variant: 'error', autoHideDuration: 5000 })
        }
    }

    const handleCancelClose = () => {
        dispatch(setOpenGuestLoginDialog(null))
        if (onCancelClose && typeof onCancelClose === 'function') {
            onCancelClose()
        }
    }

    const handleGoToLogin = () => {
        setAccessCode('')
        setError(false)
        setErrorMessage('')
        login({
            org_code: storeSettings?.kindeOrganisationId,
            ...(openGuestLoginDialog === 'checkout' && {
                app_state: { redirectTo: window.location.href.replace('review', 'checkout') },
            }),
            ...(openGuestLoginDialog === 'save-progress' && {
                app_state: { redirectTo: window.location.href },
            }),
        })
    }

    const handleGoToRegister = () => {
        setAccessCode('')
        setError(false)
        setErrorMessage('')
        register({
            org_code: storeSettings?.kindeOrganisationId,
            ...(openGuestLoginDialog === 'checkout' && {
                app_state: { redirectTo: window.location.href.replace('review', 'checkout') },
            }),
            ...(openGuestLoginDialog === 'save-progress' && {
                app_state: { redirectTo: window.location.href },
            }),
        })
    }

    return (
        <Box sx={{ width: '100%' }}>
            {loginStep === 'requestAccessCode' ? (
                <Card elevation={0}>
                    <CardHeader
                        subheader={t('Please enter your email to receive a verification code to continue.')}
                        title={t('Verify email address')}
                    />
                    <CardContent>
                        <form
                            action={handleRequestAccessCode}
                            id="guest-login-form"
                        >
                            <TextField
                                color="primary"
                                error={error}
                                helperText={errorMessage}
                                label={t('Email address')}
                                placeholder={t('Email address')}
                                size="medium"
                                sx={classes.logInInput}
                                type="email"
                                value={email}
                                variant="outlined"
                                fullWidth
                                required
                                onChange={(e) => handleEmailChange(e)}
                            />
                        </form>
                        <Box
                            display="flex"
                            gap={2}
                            justifyContent="center"
                        >
                            <Button
                                color="primary"
                                disabled={isRequesting}
                                loading={isRequesting}
                                loadingPosition="end"
                                size="large"
                                sx={classes.logInInput}
                                variant="outlined"
                                fullWidth
                                onClick={handleCancelClose}
                            >
                                {t('Cancel')}
                            </Button>
                            <Button
                                color="primary"
                                disabled={!isValidEmail(email) || isRequesting}
                                form="guest-login-form"
                                loading={isRequesting}
                                loadingPosition="end"
                                size="large"
                                sx={classes.logInInput}
                                type="submit"
                                variant="contained"
                                fullWidth
                            >
                                {t('Request Code')}
                            </Button>
                        </Box>
                    </CardContent>
                    <CardActions sx={classes.loginAction}>
                        <Typography variant="body1">{t('Already have an account?')}</Typography>
                        <Button
                            color="secondary"
                            size="small"
                            variant="text"
                            onClick={handleGoToLogin}
                        >
                            {t('Log In')}
                        </Button>
                        <Typography variant="body1">{t('or')}</Typography>
                        <Button
                            color="secondary"
                            size="small"
                            variant="text"
                            onClick={handleGoToRegister}
                        >
                            {t('Register')}
                        </Button>
                    </CardActions>
                </Card>
            ) : null}

            {loginStep === 'validateAccessCode' ? (
                <Card elevation={0}>
                    <CardHeader
                        subheader={t('Enter the access code sent to your email address.')}
                        title={t('Verify email address')}
                    />
                    <CardContent>
                        <form
                            action={handleValidateAccessCode}
                            id="guest-login-validate-form"
                        >
                            <TextField
                                color="primary"
                                label={t('Email address')}
                                placeholder={t('Email address')}
                                size="medium"
                                sx={classes.logInInput}
                                type="email"
                                value={email}
                                variant="outlined"
                                disabled
                                fullWidth
                                required
                            />
                            <TextField
                                color="primary"
                                error={error}
                                helperText={errorMessage}
                                label={t('Access code')}
                                placeholder={t('Access code')}
                                size="medium"
                                sx={classes.logInInput}
                                type="text"
                                value={accessCode}
                                variant="outlined"
                                fullWidth
                                required
                                onChange={(e) => setAccessCode(e.target.value)}
                            />
                            <Button
                                color="primary"
                                disabled={!accessCode || isValidating}
                                endIcon={<ArrowForwardRounded />}
                                form="guest-login-validate-form"
                                loading={isValidating}
                                loadingPosition="end"
                                size="large"
                                sx={classes.logInInput}
                                type="submit"
                                variant="contained"
                                fullWidth
                            >
                                {t('Verify code')}
                            </Button>
                        </form>
                    </CardContent>
                    <CardActions sx={classes.loginAction}>
                        <Typography variant="body1">{t("Didn't receive your access code?")}</Typography>
                        <Button
                            color="secondary"
                            size="small"
                            variant="text"
                            onClick={() => setLoginStep('requestAccessCode')}
                        >
                            {t('Request new access code')}
                        </Button>
                    </CardActions>
                </Card>
            ) : null}
        </Box>
    )
}

GuestLogin.propTypes = {
    onCancelClose: PropTypes.func,
    onSuccessClose: PropTypes.func,
}

export default GuestLogin
